import { useCallback, useMemo } from 'react';
import { useNotificationContext } from '../contexts/NotificationContext';
import { 
  HospitalNotification, NotificationFilter, DashboardNotificationStats,
  HospitalNotificationType, NotificationPriority, HospitalDepartment
} from '../types/hospital-notification';
import { SocketConnectionStatus } from '../types/notification';

export interface UseNotificationsReturn {
  notifications: HospitalNotification[];
  filteredNotifications: HospitalNotification[];
  stats: DashboardNotificationStats;
  filter: NotificationFilter;
  isLoading: boolean;
  error: string | null;
  connectionStatus: SocketConnectionStatus;
  unreadCount: number;
  urgentCount: number;
  isConnected: boolean;
  refreshNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  setFilter: (filter: Partial<NotificationFilter>) => void;
  connectSocket: () => Promise<void>;
  disconnectSocket: () => void;
  getNotificationsByCategory: (category: string) => HospitalNotification[];
  getNotificationsByDepartment: (department: HospitalDepartment) => HospitalNotification[];
  getNotificationsByPriority: (priority: NotificationPriority) => HospitalNotification[];
  getUnreadNotifications: () => HospitalNotification[];
  getRecentNotifications: (hours?: number) => HospitalNotification[];
  filterByType: (types: HospitalNotificationType[]) => void;
  filterByDepartment: (departments: HospitalDepartment[]) => void;
  filterByPriority: (priorities: NotificationPriority[]) => void;
  toggleUnreadOnly: () => void;
  resetFilter: () => void;
}

export const useNotifications = (): UseNotificationsReturn => {
  const context = useNotificationContext();
  
  const {
    notifications, filteredNotifications, stats, filter, isLoading, error, connectionStatus,
    refreshNotifications, markAsRead, markAllAsRead, setFilter, connectSocket, disconnectSocket
  } = context;  // 계산된 값들
  const unreadCount = useMemo(() => stats.unread, [stats.unread]);
  const urgentCount = useMemo(() => stats.urgent, [stats.urgent]);
  const isConnected = useMemo(() => 
    connectionStatus.isConnected && connectionStatus.isAuthenticated, 
    [connectionStatus]
  );

  // 병원 포털 특화 기능들
  const getNotificationsByCategory = useCallback((category: string): HospitalNotification[] => {
    return notifications.filter(n => n.category === category);
  }, [notifications]);

  const getNotificationsByDepartment = useCallback((department: HospitalDepartment): HospitalNotification[] => {
    return notifications.filter(n => n.department === department);
  }, [notifications]);

  const getNotificationsByPriority = useCallback((priority: NotificationPriority): HospitalNotification[] => {
    return notifications.filter(n => n.priority === priority);
  }, [notifications]);

  const getUnreadNotifications = useCallback((): HospitalNotification[] => {
    return notifications.filter(n => !n.isRead);
  }, [notifications]);

  const getRecentNotifications = useCallback((hours: number = 24): HospitalNotification[] => {
    const cutoffTime = new Date();
    cutoffTime.setHours(cutoffTime.getHours() - hours);
    return notifications.filter(n => new Date(n.createdAt) >= cutoffTime);
  }, [notifications]);

  // 필터 헬퍼 함수들
  const filterByType = useCallback((types: HospitalNotificationType[]) => {
    setFilter({ types });
  }, [setFilter]);

  const filterByDepartment = useCallback((departments: HospitalDepartment[]) => {
    setFilter({ departments });
  }, [setFilter]);

  const filterByPriority = useCallback((priorities: NotificationPriority[]) => {
    setFilter({ priorities });
  }, [setFilter]);

  const toggleUnreadOnly = useCallback(() => {
    setFilter({ showUnreadOnly: !filter.showUnreadOnly });
  }, [filter.showUnreadOnly, setFilter]);

  const resetFilter = useCallback(() => {
    setFilter({
      types: Object.values(HospitalNotificationType),
      departments: Object.values(HospitalDepartment),
      priorities: Object.values(NotificationPriority),
      categories: ['application', 'shift', 'staff', 'compliance', 'system'],
      showUnreadOnly: false,
      dateRange: undefined
    });
  }, [setFilter]);  return {
    // 기본 데이터
    notifications,
    filteredNotifications,
    stats,
    filter,
    isLoading,
    error,
    connectionStatus,
    
    // 계산된 값들
    unreadCount,
    urgentCount,
    isConnected,
    
    // 액션 함수들
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    setFilter,
    connectSocket,
    disconnectSocket,
    
    // 병원 포털 특화 기능
    getNotificationsByCategory,
    getNotificationsByDepartment,
    getNotificationsByPriority,
    getUnreadNotifications,
    getRecentNotifications,
    
    // 필터 헬퍼 함수들
    filterByType,
    filterByDepartment,
    filterByPriority,
    toggleUnreadOnly,
    resetFilter
  };
};

// 편의를 위한 추가 커스텀 훅들

/**
 * 긴급 알림만 가져오는 훅
 */
export const useUrgentNotifications = () => {
  const { getNotificationsByPriority } = useNotifications();
  return useMemo(() => getNotificationsByPriority(NotificationPriority.URGENT), [getNotificationsByPriority]);
};

/**
 * 특정 부서의 알림만 가져오는 훅
 */
export const useDepartmentNotifications = (department: HospitalDepartment) => {
  const { getNotificationsByDepartment } = useNotifications();
  return useMemo(() => getNotificationsByDepartment(department), [getNotificationsByDepartment, department]);
};

/**
 * 대시보드용 알림 통계 훅
 */
export const useNotificationStats = () => {
  const { stats, unreadCount, urgentCount } = useNotifications();
  return { stats, unreadCount, urgentCount };
};